import { ReparoApi } from '@api';
import { getB2bConfig } from '@api/b2b';
import {
    getSalesOrderSummary,
    listSalesOrderLines,
    listSalesOrders,
    type SalesOrderLens,
    type SalesOrderLine,
    type SalesOrderListItem,
    type SalesOrderListRequest,
    type SalesOrderSummary,
} from '@api/salesOrders';
import PageHeader from '@components/common/PageHeader';
import AskAiButton from '@components/QuickHelpPanel/AskAiButton';
import { DataSheet, DataSheetPagination, dataSheetTokens, type DataSheetColumn } from '@components/ui/DataSheet';
import { DatePopover } from '@components/ui/DateRange';
import FilterDropdown, { type DropdownOption } from '@components/ui/Dropdown';
import StatCard from '@components/ui/StatCard';
import { useWidgetContextCapture } from '@hooks/useWidgetContextCapture';
import { Button, SegmentedControl } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import useApp from '@providers/App/useApp';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { differenceInCalendarDays } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { ConsultantCapability } from '../../../constants/consultant-capability';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import usePersistentState from '../../../hooks/usePersistentState';
import { defaultLast4Weeks, formatDisplayRange } from '../demand/dateUtils';
import { StatusBadge } from '../procurementOrders/shared';
import { formatDateDisplay } from '../PurchaseOrderDetailDrawer';
import { MoneyPair, TwoLine } from './cells';
import { formatRoundMoney, formatUnits } from './format';
import styles from './SalesOrders.module.css';
import { currencySymbol, formatCompact, formatCount } from '@utils/currency';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

type SheetTab = 'orders' | 'lines';
type LensValue = 'all' | SalesOrderLens;

interface ChannelApiResponse {
    channelId?: string;
    abbreviationKey?: string;
    name?: string;
    type?: string;
}

const tokens = dataSheetTokens;
const AMBER = 'rgb(202, 138, 4)';

/* -------------------------------------------------------------------------- */
/*                                   Cells                                    */
/* -------------------------------------------------------------------------- */

/** A card's helper text with the value of the orders it counts, when any of them is priced. */
const withValue = (text: string, value: number | undefined): string =>
    value && value > 0 ? `${text} · ${currencySymbol()}${formatCompact(value)}` : text;

/** Expiry with a days-left hint; urgency colour follows the design system's semantic palette. */
const ExpiryCell: React.FC<{ value?: string }> = ({ value }) => {
    if (!value) return <span style={{ color: neutralColors.textMuted }}>—</span>;
    const days = differenceInCalendarDays(new Date(value), new Date());
    const hint = days < 0 ? `${-days}d ago` : days === 0 ? 'today' : `in ${days}d`;
    const color =
        days <= 1 ? semanticColors.critical.primary : days <= 3 ? AMBER : neutralColors.textMuted;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.3 }}>
            <span>{formatDateDisplay(value)}</span>
            <span style={{ fontSize: 11, color }}>{hint}</span>
        </div>
    );
};

/** Dispatched over ordered, with a 4px bar - the quantity truth beside the status. */
const FillRateCell: React.FC<{ ordered: number; dispatched: number }> = ({ ordered, dispatched }) => {
    if (!ordered) return <span style={{ color: neutralColors.textMuted }}>—</span>;
    const pct = Math.round((100 * dispatched) / ordered);
    const color =
        pct >= 100 ? semanticColors.positive.primary : pct >= 70 ? semanticColors.warning.primary : semanticColors.critical.primary;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
            <div style={{ width: 56, height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(pct, 100)}%`, height: 4, background: color }} />
            </div>
        </div>
    );
};

const numberCell = (value: number | undefined | null) => (
    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatUnits(value)}</span>
);

/* -------------------------------------------------------------------------- */
/*                                   Page                                     */
/* -------------------------------------------------------------------------- */

const SalesOrdersContainer: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useCurrentUser();
    const { consultantAccess } = useApp();
    const isConsultant = currentUser?.role === 'CONSULTANT';
    // Sales orders are the demand-side rows the consultant capability already names.
    const hasAccess = !isConsultant || consultantAccess.includes(ConsultantCapability.TABLE_PO_DEMAND);

    const { captureContext, addToChat } = useWidgetContextCapture({
        componentName: 'SalesOrdersTable',
        label: 'Sales Orders',
    });

    const [tab, setTab] = usePersistentState<SheetTab>('so_tab', 'orders');
    const [filters, setFilters] = usePersistentState('so_filters', {
        range: defaultLast4Weeks(),
        search: '',
        channel: 'all',
        /** all | marketplace | b2b: B2B customers are the channels in b2b_config.channelKeys. */
        source: 'all',
        lens: 'all' as LensValue,
        page: 1,
        pageSize: 25,
    });
    const [showCalendar, setShowCalendar] = useState(false);

    const [channelOptions, setChannelOptions] = useState<DropdownOption[]>([{ label: 'All channels', value: 'all' }]);
    const [summary, setSummary] = useState<SalesOrderSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);

    const [orders, setOrders] = useState<SalesOrderListItem[]>([]);
    const [lines, setLines] = useState<SalesOrderLine[]>([]);
    const [total, setTotal] = useState(0);
    const [unclassifiedCount, setUnclassifiedCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const { data: b2bConfig } = useQuery({ queryKey: ['b2b-config'], queryFn: getB2bConfig, staleTime: 5 * 60 * 1000, retry: false });
    const b2bKeys = useMemo(() => new Set(b2bConfig?.channelKeys ?? []), [b2bConfig]);
    /** The channels the list and summary ask for: one channel, a source, or everything. */
    const channelScope = useMemo((): string[] | undefined => {
        if (filters.channel !== 'all') return [filters.channel];
        const source = filters.source ?? 'all';
        if (source === 'b2b') return b2bKeys.size ? [...b2bKeys] : ['__none__'];
        if (source === 'marketplace') {
            const others = channelOptions.map((o) => String(o.value)).filter((v) => v !== 'all' && !b2bKeys.has(v));
            return others.length ? others : ['__none__'];
        }
        return undefined;
    }, [filters.channel, filters.source, b2bKeys, channelOptions]);

    const channelLabelLookup = useMemo(
        () => new Map(channelOptions.filter((o) => o.value !== 'all').map((o) => [String(o.value), o.label])),
        [channelOptions],
    );

    /** The request the list, lines and download share; the summary only takes the channel. */
    const buildRequest = useCallback(
        (extra?: Partial<SalesOrderListRequest>): SalesOrderListRequest => ({
            startDate: filters.range.startDate,
            endDate: filters.range.endDate,
            channels: channelScope,
            lens: filters.lens === 'all' ? undefined : filters.lens,
            search: filters.search || undefined,
            page: filters.page,
            pageSize: filters.pageSize,
            ...extra,
        }),
        [filters, channelScope],
    );

    /* ------------------------------- Fetchers ------------------------------- */

    useEffect(() => {
        const fetchChannels = async () => {
            try {
                const channels =
                    ((await ReparoApi({ method: 'GET', url: '/channels?limit=200', body: undefined })) as
                        | ChannelApiResponse[]
                        | null) ?? [];
                const mapped = channels
                    .filter((channel) => channel.type === 'B2B')
                    .map((channel) => {
                        const key = channel.abbreviationKey || channel.channelId || channel.name;
                        // B2B customer channels are keyed by a vendor id; their name reads better.
                        const label = (channel.abbreviationKey?.startsWith('B2B-') ? channel.name : null) || channel.abbreviationKey || channel.name || channel.channelId;
                        return key && label ? ({ label, value: String(key) } as DropdownOption) : null;
                    })
                    .filter((opt): opt is DropdownOption => Boolean(opt));
                const deduped = Array.from(new Map(mapped.map((opt) => [opt.value, opt])).values());
                setChannelOptions([{ label: 'All channels', value: 'all' }, ...deduped]);
            } catch {
                setChannelOptions([{ label: 'All channels', value: 'all' }]);
            }
        };
        void fetchChannels();
    }, []);

    useEffect(() => {
        if (!hasAccess) return;
        let cancelled = false;
        setSummaryLoading(true);
        getSalesOrderSummary({ channels: channelScope })
            .then((result) => {
                if (!cancelled) setSummary(result);
            })
            .catch((error) => console.error('Failed to fetch sales order summary', error))
            .finally(() => {
                if (!cancelled) setSummaryLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [channelScope, hasAccess]);

    useEffect(() => {
        if (!hasAccess) return;
        let cancelled = false;
        setLoading(true);
        const request = buildRequest();
        const run = async () => {
            try {
                if (tab === 'orders') {
                    const result = await listSalesOrders(request);
                    if (cancelled) return;
                    setOrders(result.data);
                    setTotal(result.total);
                    setUnclassifiedCount(result.unclassifiedCount);
                } else {
                    const result = await listSalesOrderLines(request);
                    if (cancelled) return;
                    setLines(result.data);
                    setTotal(result.total);
                }
                captureContext({ tab, ...request } as unknown as Record<string, unknown>);
            } catch (error) {
                console.error('Failed to fetch sales orders', error);
                if (!cancelled) {
                    setOrders([]);
                    setLines([]);
                    setTotal(0);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [buildRequest, captureContext, hasAccess, tab]);

    /* ------------------------------- Columns -------------------------------- */

    const openOrder = useCallback(
        (purchaseOrderId: string) => {
            void navigate(`/sales-orders/${encodeURIComponent(purchaseOrderId)}`);
        },
        [navigate],
    );

    const orderLink = useCallback(
        (purchaseOrderId: string) => (
            <button
                type="button"
                onClick={(event) => {
                    event.stopPropagation();
                    openOrder(purchaseOrderId);
                }}
                style={{
                    fontWeight: 600,
                    color: 'rgb(3, 105, 161)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    textAlign: 'left',
                }}
            >
                {purchaseOrderId}
            </button>
        ),
        [openOrder],
    );

    const orderColumns = useMemo<DataSheetColumn<SalesOrderListItem>[]>(
        () => [
            {
                key: 'purchaseOrderId',
                header: 'Order #',
                width: 170,
                sticky: true,
                render: (value: string, row) => <TwoLine primary={orderLink(value)} secondary={row.poRefNum} />,
            },
            {
                key: 'channel',
                header: 'Channel',
                width: 120,
                render: (value: string | undefined) => channelLabelLookup.get(value ?? '') || value || '—',
            },
            {
                key: 'destinationName',
                header: 'Destination',
                width: 170,
                render: (value: string | undefined, row) => value || row.warehouseId || '—',
            },
            { key: 'orderDate', header: 'Order date', width: 110, render: (value: string) => formatDateDisplay(value) },
            { key: 'expiryDate', header: 'Expires', width: 120, render: (value?: string) => <ExpiryCell value={value} /> },
            {
                key: 'derivedStatus',
                header: 'Status',
                width: 160,
                render: (value: string | undefined, row) => <StatusBadge status={value ?? row.status} />,
            },
            { key: 'totalQuantity', header: 'Ordered', width: 100, align: 'right', render: numberCell },
            { key: 'actualQuantity', header: 'Dispatched', width: 110, align: 'right', render: numberCell },
            { key: 'partnerReceivedQuantity', header: 'Received', width: 100, align: 'right', render: numberCell },
            {
                key: 'fillRate',
                header: 'Fill rate',
                width: 110,
                align: 'right',
                render: (_: unknown, row) => <FillRateCell ordered={row.totalQuantity} dispatched={row.actualQuantity ?? 0} />,
            },
            {
                // The whole order, as the partner priced it. Unit prices differ by line, so
                // they live on the order's own page.
                key: 'orderValue',
                header: 'Order value',
                width: 160,
                align: 'right',
                render: (value: number | null | undefined, row) => (
                    <MoneyPair
                        beforeTax={value}
                        withTax={row.orderValueWithTax}
                        note={
                            value != null && (row.pricedItemsCount ?? 0) < row.itemsCount
                                ? `${row.pricedItemsCount ?? 0} of ${row.itemsCount} lines priced`
                                : undefined
                        }
                    />
                ),
            },
            {
                key: 'returnedUnits',
                header: 'Returns / DN',
                width: 140,
                render: (value: number | undefined, row) =>
                    value && value > 0 ? (
                        <TwoLine primary={`${formatUnits(value)} units`} secondary={row.returnedValue ? formatRoundMoney(row.returnedValue) : undefined} />
                    ) : (
                        <span style={{ color: neutralColors.textMuted }}>—</span>
                    ),
            },
            { key: 'dispatchDate', header: 'Dispatch date', width: 120, render: (value?: string) => (value ? formatDateDisplay(value) : '—') },
        ],
        [channelLabelLookup, orderLink],
    );

    const lineColumns = useMemo<DataSheetColumn<SalesOrderLine>[]>(
        () => [
            { key: 'purchaseOrderId', header: 'Order #', width: 160, sticky: true, render: (value: string) => orderLink(value) },
            {
                key: 'channel',
                header: 'Channel',
                width: 110,
                render: (value: string | undefined) => channelLabelLookup.get(value ?? '') || value || '—',
            },
            { key: 'orderDate', header: 'Order date', width: 110, render: (value: string) => formatDateDisplay(value) },
            { key: 'lineStatus', header: 'Line status', width: 150, render: (value: string) => <StatusBadge status={value} /> },
            {
                key: 'name',
                header: 'Product',
                width: 260,
                render: (value: string | undefined, row) => <TwoLine primary={value || row.productId} secondary={row.sku} />,
            },
            { key: 'orderedQty', header: 'Ordered', width: 90, align: 'right', render: numberCell },
            { key: 'dispatchedQty', header: 'Dispatched', width: 100, align: 'right', render: numberCell },
            { key: 'partnerReceivedQty', header: 'Received', width: 100, align: 'right', render: (value: number | null) => numberCell(value ?? 0) },
            {
                key: 'shortQty',
                header: 'Short',
                width: 80,
                align: 'right',
                render: (value: number) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums', color: value > 0 ? semanticColors.critical.primary : neutralColors.textMuted }}>
                        {formatUnits(value)}
                    </span>
                ),
            },
            {
                key: 'fill',
                header: 'Fill',
                width: 100,
                align: 'right',
                render: (_: unknown, row) => <FillRateCell ordered={row.orderedQty} dispatched={row.dispatchedQty} />,
            },
            {
                key: 'returnedUnits',
                header: 'Returned',
                width: 90,
                align: 'right',
                render: (value: number | undefined) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums', color: value && value > 0 ? semanticColors.critical.primary : neutralColors.textMuted }}>
                        {formatUnits(value ?? 0)}
                    </span>
                ),
            },
        ],
        [channelLabelLookup, orderLink],
    );

    /* ------------------------------- Download ------------------------------- */

    const handleDownload = useCallback(async () => {
        const request = buildRequest({ fetchAll: true, page: 1 });
        const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
        let headers: string[];
        let rows: unknown[][];
        if (tab === 'orders') {
            const result = await listSalesOrders(request);
            headers = ['Order', 'Ref', 'Channel', 'Destination', 'Order date', 'Expires', 'Status', 'Ordered', 'Dispatched', 'Received', 'Fill rate %', 'Order value (before tax)', 'Order value (with tax)', 'Returned units', 'Returned value', 'Dispatch date'];
            rows = result.data.map((row) => [
                row.purchaseOrderId,
                row.poRefNum ?? '',
                channelLabelLookup.get(row.channel ?? '') || row.channel || '',
                row.destinationName ?? row.warehouseId ?? '',
                formatDateDisplay(row.orderDate),
                row.expiryDate ? formatDateDisplay(row.expiryDate) : '',
                row.derivedStatus ?? row.status,
                row.totalQuantity,
                row.actualQuantity ?? 0,
                row.partnerReceivedQuantity ?? '',
                row.totalQuantity ? Math.round((100 * (row.actualQuantity ?? 0)) / row.totalQuantity) : '',
                row.orderValue ?? '',
                row.orderValueWithTax ?? '',
                row.returnedUnits ?? 0,
                row.returnedValue ?? 0,
                row.dispatchDate ? formatDateDisplay(row.dispatchDate) : '',
            ]);
        } else {
            const result = await listSalesOrderLines(request);
            headers = ['Order', 'Channel', 'Order date', 'Line status', 'Product', 'SKU', 'Ordered', 'Dispatched', 'Received', 'Short', 'Fill %', 'Returned'];
            rows = result.data.map((row) => [
                row.purchaseOrderId,
                channelLabelLookup.get(row.channel ?? '') || row.channel || '',
                formatDateDisplay(row.orderDate),
                row.lineStatus,
                row.name ?? row.productId,
                row.sku ?? '',
                row.orderedQty,
                row.dispatchedQty,
                // Partner-reported receipt; blank until the partner's feed carries a figure for the line.
                row.partnerReceivedQty ?? '',
                row.shortQty,
                row.orderedQty ? Math.round((100 * row.dispatchedQty) / row.orderedQty) : '',
                row.returnedUnits ?? 0,
            ]);
        }
        if (rows.length === 0) return;
        const csv = [headers.map(escape).join(','), ...rows.map((row) => row.map(escape).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = tab === 'orders' ? 'sales_orders.csv' : 'sales_order_lines.csv';
        link.click();
        URL.revokeObjectURL(url);
    }, [buildRequest, channelLabelLookup, tab]);

    /* -------------------------------- Lenses -------------------------------- */

    const byStatus = summary?.byStatus;
    const totalOrders = byStatus ? Object.values(byStatus).reduce((sum, n) => sum + n, 0) : 0;
    const lensData = useMemo(
        () => [
            { value: 'all', label: `All (${totalOrders})` },
            { value: 'open', label: `Open (${byStatus?.OPEN ?? 0})` },
            { value: 'partially_dispatched', label: `Partially dispatched (${byStatus?.PARTIALLY_DISPATCHED ?? 0})` },
            { value: 'dispatched', label: `Dispatched (${byStatus?.DISPATCHED ?? 0})` },
            { value: 'fulfilled', label: `Fulfilled (${byStatus?.FULFILLED ?? 0})` },
            { value: 'lapsed', label: `Lapsed (${byStatus?.LAPSED ?? 0})` },
            { value: 'short', label: `Short (${summary?.shortOrders ?? 0})` },
            { value: 'cancelled_or_expired', label: `Cancelled / expired (${(byStatus?.CANCELLED ?? 0) + (byStatus?.EXPIRED ?? 0)})` },
        ],
        [byStatus, summary?.shortOrders, totalOrders],
    );

    const setLens = (lens: LensValue) => setFilters((prev) => ({ ...prev, page: 1, lens }));

    if (!hasAccess) {
        return (
            <div className="flex h-full w-full flex-col page-shell">
                <PageHeader icon="🧾" title="Sales Orders" subtitle="You do not have access to this table." />
            </div>
        );
    }

    const subtitleParts = [`${formatCount(total)} ${tab === 'orders' ? 'orders' : 'lines'}`];
    if (unclassifiedCount > 0) subtitleParts.push(`${formatCount(unclassifiedCount)} unclassified`);

    return (
        <div className="flex h-full w-full flex-col gap-4 page-shell">
            <PageHeader
                className={styles.header}
                icon="🧾"
                title="Sales Orders"
                subtitle="Orders raised by marketplaces and B2B partners, and what you fulfilled against them"
                actions={
                    <div className="flex items-center gap-2">
                        {b2bConfig?.enabled ? (
                            <Button variant={ButtonVariant.ACTION} onClick={() => navigate('/b2b/orders/new')}>
                                New B2B order
                            </Button>
                        ) : null}
                        <AskAiButton />
                    </div>
                }
            />

            {/* KPI strip - every figure is a lens the grid can open. */}
            <div className="grid grid-cols-4 gap-4">
                <StatCard
                    label="Open orders"
                    value={summary?.openOrders ?? '—'}
                    helper={withValue('awaiting dispatch', summary?.openValue)}
                    loading={summaryLoading}
                    onClick={() => setLens('open')}
                />
                <StatCard
                    label="Expiring in 48h"
                    value={summary?.expiringIn48h ?? '—'}
                    helper={withValue('not yet fully dispatched', summary?.expiringIn48hValue)}
                    valueColor={summary && summary.expiringIn48h > 0 ? semanticColors.critical.primary : undefined}
                    loading={summaryLoading}
                />
                <StatCard
                    label="Partially dispatched"
                    value={summary?.partiallyDispatched ?? '—'}
                    helper={withValue('dispatched short of ordered', summary?.partiallyDispatchedValue)}
                    valueColor={summary && summary.partiallyDispatched > 0 ? AMBER : undefined}
                    loading={summaryLoading}
                    onClick={() => setLens('partially_dispatched')}
                />
                <StatCard
                    label="Fill rate · 30 days"
                    value={summary?.fillRate30d === null || summary?.fillRate30d === undefined ? '—' : `${summary.fillRate30d}%`}
                    helper="dispatched ÷ ordered units"
                    loading={summaryLoading}
                />
            </div>

            {/* Status lens - derived from the partner status and our dispatched units (PRD section 5). */}
            <SegmentedControl
                size="xs"
                value={filters.lens}
                onChange={(value) => setLens(value as LensValue)}
                data={lensData}
            />

            <DataSheet<SalesOrderListItem | SalesOrderLine>
                className="flex-1 min-h-0"
                data={tab === 'orders' ? orders : lines}
                columns={(tab === 'orders' ? orderColumns : lineColumns) as DataSheetColumn<SalesOrderListItem | SalesOrderLine>[]}
                tabs={[
                    { id: 'orders', label: 'Orders' },
                    { id: 'lines', label: 'Line items' },
                ]}
                activeTab={tab}
                onTabChange={(tabId) => {
                    setTab(tabId as SheetTab);
                    setFilters((prev) => ({ ...prev, page: 1 }));
                }}
                title={tab === 'orders' ? 'Sales orders' : 'Line items'}
                subtitle={subtitleParts.join(' · ')}
                onAddToChat={addToChat}
                enableDownload
                onDownload={handleDownload}
                enableSearch
                searchPlaceholder={tab === 'orders' ? 'Search order #, ref…' : 'Search order #, SKU, product…'}
                searchValue={filters.search}
                onSearchChange={(value) => setFilters((prev) => ({ ...prev, page: 1, search: value }))}
                toolbarControls={
                    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing.xs }}>
                        <div style={{ position: 'relative' }}>
                            <button
                                type="button"
                                onClick={() => setShowCalendar((prev) => !prev)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: tokens.spacing.xs,
                                    height: tokens.dimensions.controlHeight,
                                    padding: `0 ${tokens.spacing.sm}px`,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: tokens.colors.textPrimary,
                                    backgroundColor: tokens.colors.surface,
                                    border: `1px solid ${tokens.colors.border}`,
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                }}
                            >
                                <span style={{ fontSize: 12 }}>📅</span>
                                <span>{formatDisplayRange(filters.range)}</span>
                            </button>
                            {showCalendar && (
                                <DatePopover
                                    range={filters.range}
                                    onClose={() => setShowCalendar(false)}
                                    onApply={(range) => {
                                        setFilters((prev) => ({ ...prev, page: 1, range }));
                                        setShowCalendar(false);
                                    }}
                                />
                            )}
                        </div>
                        {b2bKeys.size > 0 ? (
                            <FilterDropdown
                                value={filters.source ?? 'all'}
                                onChange={(value) => setFilters((prev) => ({ ...prev, page: 1, source: value }))}
                                options={[
                                    { label: 'All sources', value: 'all' },
                                    { label: 'Marketplaces', value: 'marketplace' },
                                    { label: 'B2B customers', value: 'b2b' },
                                ]}
                                placeholder="Source"
                            />
                        ) : null}
                        <FilterDropdown
                            value={filters.channel}
                            onChange={(value) => setFilters((prev) => ({ ...prev, page: 1, channel: value }))}
                            options={channelOptions}
                            placeholder="Channel"
                        />
                    </div>
                }
                isLoading={loading}
                maxHeight="auto"
                getRowKey={(row, index) =>
                    'productId' in row ? `${row.purchaseOrderId}|${row.productId}|${index}` : row.purchaseOrderId
                }
                onRowClick={(row) => openOrder(row.purchaseOrderId)}
                emptyState={tab === 'orders' ? 'No sales orders match these filters.' : 'No order lines match these filters.'}
                footer={
                    <DataSheetPagination
                        currentPage={filters.page}
                        totalPages={Math.ceil(total / filters.pageSize) || 1}
                        pageSize={filters.pageSize}
                        totalItems={total}
                        pageSizeOptions={[25, 50, 100]}
                        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
                        onPageSizeChange={(pageSize) => setFilters((prev) => ({ ...prev, page: 1, pageSize }))}
                    />
                }
            />
        </div>
    );
};

export default SalesOrdersContainer;
